// One HTTP client used by every caller (CLI tools, dashboard, smoke tests).
// Wraps fetch, injects auth + on-behalf-of, returns typed Result instead of
// throwing on non-2xx — only network failures throw upward.

import type { ApiError, Result } from "./result"
import { err, ok } from "./result"

export type ApiFetchOptions = {
  baseUrl: string
  bearerToken: string
  onBehalfOf?: string
  // Set to "include" for browser session-cookie auth (dashboard).
  credentials?: RequestCredentials
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  headers?: Record<string, string>
  signal?: AbortSignal
}

export function createApiFetch(config: ApiFetchOptions) {
  return async function apiFetch<T>(
    path: string,
    opts: RequestOptions = {}
  ): Promise<Result<T, ApiError>> {
    const url = buildUrl(config.baseUrl, path, opts.query)
    const headers = new Headers(opts.headers ?? {})
    // Empty bearerToken means "rely on the session cookie" — used by the dashboard.
    if (config.bearerToken) {
      headers.set("Authorization", `Bearer ${config.bearerToken}`)
    }
    if (config.onBehalfOf) {
      headers.set("X-On-Behalf-Of", config.onBehalfOf)
    }
    if (opts.body !== undefined) {
      headers.set("Content-Type", "application/json")
    }

    const init: RequestInit = {
      method: opts.method ?? "GET",
      headers,
    }
    if (config.credentials !== undefined) {
      init.credentials = config.credentials
    }
    if (opts.body !== undefined) {
      init.body = JSON.stringify(opts.body)
    }
    if (opts.signal !== undefined) {
      init.signal = opts.signal
    }

    let response: Response
    try {
      response = await fetch(url, init)
    } catch (cause) {
      if (opts.signal?.aborted) {
        return err(abortedError(opts.signal))
      }
      return err({
        kind: "network",
        message: cause instanceof Error ? cause.message : String(cause),
      })
    }

    return parseResponse<T>(response, opts.signal)
  }
}

export type ApiFetch = ReturnType<typeof createApiFetch>

async function parseResponse<T>(
  response: Response,
  signal: AbortSignal | undefined
): Promise<Result<T, ApiError>> {
  const contentType = response.headers.get("content-type") ?? ""
  const isJson = contentType.includes("application/json")
  let parsed: unknown = null
  if (isJson) {
    try {
      parsed = await response.json()
    } catch {
      if (signal?.aborted) {
        return err(abortedError(signal))
      }
      // fall through — treat as text below
    }
  }

  if (response.ok) {
    return ok(parsed as T)
  }

  const fallback: ApiError = {
    kind: mapStatusToKind(response.status),
    message: `HTTP ${response.status}`,
    status: response.status,
  }
  if (isApiErrorBody(parsed)) {
    return err({
      kind: parsed.error.kind,
      message: parsed.error.message,
      details: parsed.error.details,
      status: response.status,
    })
  }
  return err(fallback)
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: Record<string, string | number | boolean | undefined> | undefined
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, "")
  const trimmedPath = path.startsWith("/") ? path : `/${path}`
  let url = `${trimmedBase}${trimmedPath}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        params.set(key, String(value))
      }
    }
    const qs = params.toString()
    if (qs) {
      url += `?${qs}`
    }
  }
  return url
}

/** The INBOUND mapping: how a client reads a status the Nova API returned. The
 *  kinds are written from that perspective — `unavailable` in particular means
 *  "the service we called could not serve us", which reads correctly only when
 *  that service is ours. A caller classifying a THIRD PARTY's response (a
 *  marketplace, Boxpi) should map its 5xx to `upstream` itself rather than
 *  reusing this function, or the resulting kind blames Nova for someone else's
 *  outage. */
export function mapStatusToKind(status: number): ApiError["kind"] {
  if (status === 400) {
    return "validation"
  }
  if (status === 401) {
    return "unauthorized"
  }
  if (status === 403) {
    return "forbidden"
  }
  if (status === 404) {
    return "not_found"
  }
  if (status === 409) {
    return "conflict"
  }
  if (status === 422) {
    return "illegal_transition"
  }
  // 503 is the API telling us it is out of capacity right now (a DB statement
  // it cancelled, a starved pool — see ApiErrorKind.unavailable); every other
  // 5xx is treated as a failure further upstream.
  if (status === 503) {
    return "unavailable"
  }
  if (status >= 500) {
    return "upstream"
  }
  return "unknown"
}

/** Build the canonical `aborted` ApiError from an aborted signal's reason — the
 *  single source of truth so the transport (`createApiFetch`) and the CLI retry
 *  layer (`requestJson`) report identical shapes for the same abort. */
export function abortedError(signal: AbortSignal): ApiError {
  const reason = signal.reason
  if (reason instanceof Error) {
    return { kind: "aborted", message: reason.message, details: { name: reason.name } }
  }
  if (typeof reason === "string") {
    return { kind: "aborted", message: reason, details: reason }
  }
  return { kind: "aborted", message: "Request aborted", details: reason }
}

function isApiErrorBody(
  value: unknown
): value is { error: { kind: ApiError["kind"]; message: string; details?: unknown } } {
  if (typeof value !== "object" || value === null) {
    return false
  }
  const v = value as Record<string, unknown>
  if (typeof v.error !== "object" || v.error === null) {
    return false
  }
  const e = v.error as Record<string, unknown>
  return typeof e.kind === "string" && typeof e.message === "string"
}
